import { CopyOutlined, DisconnectOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import {
  api,
  type EventSpeakerRow,
  type SlideLicenseRow,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';

const { Text } = Typography;

type AppEntry = {
  id: string;
  name: string;
  primary: boolean;
  dominant: boolean;
  slideNumber: number | null;
  totalSlides: number | null;
};

type IncomingMessage =
  | { type: 'app-status'; connected: boolean }
  | { type: 'app-list'; apps: AppEntry[]; conflict: boolean }
  | { type: 'state'; enabled: boolean }
  | { type: 'notes'; text: string; slideNumber: number; totalSlides: number };

const NEW_LICENSE_KEY = '__new__';

export function SlideControlPanel({
  open,
  speakerName,
  eventSpeaker,
  onClose,
  onChange,
}: {
  open: boolean;
  speakerName: string;
  eventSpeaker: Pick<
    EventSpeakerRow,
    | 'id'
    | 'event_id'
    | 'slide_license_id'
    | 'slide_license_name'
    | 'slide_license_key'
    | 'slide_control_enabled'
  >;
  onClose: () => void;
  onChange: () => Promise<void> | void;
}) {
  const { session } = useAuth();
  const [busy, setBusy] = useState(false);
  const [licenses, setLicenses] = useState<SlideLicenseRow[]>([]);
  const [newLicenseOpen, setNewLicenseOpen] = useState(false);
  const [newLicenseName, setNewLicenseName] = useState('');

  const licenseId = eventSpeaker.slide_license_id ?? null;
  const licenseName = eventSpeaker.slide_license_name ?? null;
  const licenseKey = eventSpeaker.slide_license_key ?? null;

  useEffect(() => {
    if (!open) return;
    void (async () => {
      try {
        setLicenses(await api<SlideLicenseRow[]>('/api/slide-licenses'));
      } catch {
        setLicenses([]);
      }
    })();
  }, [open]);

  const adminWsUrl = useMemo(() => {
    if (!open || !licenseKey || !session) return null;
    return wsUrl(
      `/api/slides/${encodeURIComponent(licenseKey)}?role=admin&token=${encodeURIComponent(session.token)}`,
    );
  }, [open, licenseKey, session]);

  const [appConnected, setAppConnected] = useState(false);
  const [slideMeta, setSlideMeta] = useState<{ n: number; t: number } | null>(null);
  const [apps, setApps] = useState<AppEntry[]>([]);
  const [conflict, setConflict] = useState(false);
  const { send: sendAdmin } = useWebSocket<IncomingMessage>(adminWsUrl, (msg) => {
    if (msg.type === 'app-status') setAppConnected(msg.connected);
    else if (msg.type === 'notes') setSlideMeta({ n: msg.slideNumber, t: msg.totalSlides });
    else if (msg.type === 'app-list') {
      setApps(msg.apps);
      setConflict(msg.conflict);
    }
  });

  const makePrimary = (appId: string) => {
    sendAdmin({ type: 'set-primary', appId });
  };

  const setLicense = async (id: string | null) => {
    setBusy(true);
    try {
      await api(
        `/api/events/${eventSpeaker.event_id}/speakers/${eventSpeaker.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ slide_license_id: id }),
        },
      );
      await onChange();
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to update');
    } finally {
      setBusy(false);
    }
  };

  const createLicense = async () => {
    if (!newLicenseName.trim()) {
      message.warning('Give the license a name');
      return;
    }
    setBusy(true);
    try {
      const created = await api<SlideLicenseRow>('/api/slide-licenses', {
        method: 'POST',
        body: JSON.stringify({ name: newLicenseName.trim() }),
      });
      setLicenses((prev) => [...prev, created]);
      setNewLicenseOpen(false);
      setNewLicenseName('');
      await setLicense(created.id);
    } catch (err) {
      message.error((err as { error?: string }).error ?? 'Failed to create');
    } finally {
      setBusy(false);
    }
  };

  const copyKey = async () => {
    if (!licenseKey) return;
    await navigator.clipboard.writeText(licenseKey);
    message.success('Copied');
  };

  return (
    <Modal
      title={`Slide control — ${speakerName}`}
      open={open}
      onCancel={onClose}
      footer={
        <Button type="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Text type="secondary">
          Pick the machine (slide license) this speaker is using. Speakers sharing
          one Keynote deck on the same laptop should all point at the same
          license — no need to mint a new one per speaker.
        </Text>

        <Form layout="vertical">
          <Form.Item label="Slide license">
            <Select
              value={licenseId ?? null}
              loading={busy}
              onChange={(val: string | null) => {
                if (val === NEW_LICENSE_KEY) {
                  setNewLicenseOpen(true);
                  return;
                }
                void setLicense(val);
              }}
              placeholder="Not assigned"
              allowClear
              onClear={() => void setLicense(null)}
              options={[
                ...licenses.map((l) => ({ value: l.id, label: l.name })),
                {
                  value: NEW_LICENSE_KEY,
                  label: (
                    <span>
                      <PlusOutlined /> Create new license…
                    </span>
                  ),
                },
              ]}
            />
          </Form.Item>
        </Form>

        {licenseId && licenseKey && (
          <>
            <Alert
              type="info"
              showIcon
              message={`Paste this key into the AOTV Slide Control app on ${licenseName ?? 'the slides machine'}.`}
            />
            <div
              style={{
                padding: '14px 18px',
                background: '#fafbfc',
                border: '1px solid #e5eaf0',
                borderRadius: 6,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 20,
                letterSpacing: 1,
                textAlign: 'center',
              }}
            >
              {licenseKey}
            </div>
            <Space>
              <Button icon={<CopyOutlined />} onClick={copyKey}>
                Copy key
              </Button>
              <Button
                icon={<DisconnectOutlined />}
                danger
                onClick={() => void setLicense(null)}
                loading={busy}
              >
                Turn off for this speaker
              </Button>
            </Space>
            <Space>
              <Text type="secondary">Slide Control app:</Text>
              <Tag color={appConnected ? 'green' : 'default'}>
                {appConnected ? 'Connected' : 'Not connected'}
              </Tag>
              {slideMeta && (
                <Text type="secondary">
                  Slide {slideMeta.n} / {slideMeta.t}
                </Text>
              )}
            </Space>

            {apps.length > 0 && (
              <div
                style={{
                  border: '1px solid #e5eaf0',
                  borderRadius: 6,
                  padding: 12,
                }}
              >
                {conflict && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Two machines are both claiming primary."
                    description="The earliest connection wins for the speaker's notes. Use the buttons below to override."
                    style={{ marginBottom: 12 }}
                  />
                )}
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  {apps.map((app) => (
                    <div
                      key={app.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                      }}
                    >
                      <Space size={6}>
                        <Tag color={app.dominant ? 'green' : 'default'}>
                          {app.dominant ? 'Notes source' : app.primary ? 'Primary' : 'Backup'}
                        </Tag>
                        <Text>{app.name}</Text>
                        {app.slideNumber != null && app.totalSlides != null && (
                          <Text type="secondary">
                            Slide {app.slideNumber} / {app.totalSlides}
                          </Text>
                        )}
                      </Space>
                      {!app.dominant && (
                        <Button
                          size="small"
                          onClick={() => makePrimary(app.id)}
                        >
                          Make notes source
                        </Button>
                      )}
                    </div>
                  ))}
                </Space>
              </div>
            )}
          </>
        )}
      </Space>

      <Modal
        title="New slide license"
        open={newLicenseOpen}
        onCancel={() => setNewLicenseOpen(false)}
        onOk={createLicense}
        confirmLoading={busy}
        okText="Create"
      >
        <Form layout="vertical">
          <Form.Item
            label="Name"
            extra="Identify the machine. e.g. 'Studio Mac mini' or 'Stage A show PC'."
          >
            <Input
              value={newLicenseName}
              onChange={(e) => setNewLicenseName(e.target.value)}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </Modal>
  );
}
